import { Route, Routes, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { ProductPending, ProductPaused, Waitlist, FeatureLayout } from '@lendica/components';
import { WithdrawalConfirmation, Withdrawal, WithdrawalFailure, WithdrawalSuccess } from './views';
import { DrawDownView } from './DrawDownView';
import { useEffect, useState } from 'react';
import { useDrawDownApi } from './api';
import { useCompanyNavigate } from '@lendica/hooks';

// Potentially moving setError to its' own container would be a cleaner solution for use in different apps
export const DrawDown = ({ placeholder }) => {
    const navigate = useCompanyNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(true);
    const [approval, setApproval] = useState(null);
    const [rate, setRate] = useState(0);
    const [rates, setRates] = useState([]);
    const [dealCreated, setDealCreated] = useState(false);
    const [withdrawalData, setWithdrawalData] = useState({
        withdrawal_amount: 0,
        net_funded_amount: 0,
        weekly_pmt: 0,
        total_pmt: 0,
        factor_rate: 0,
        origination_fee_percentage: 0,
        term_length: 0,
    });
    const api = useDrawDownApi();

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        const getInfo = async () => {
            try {
                // First check company drawdown status and route accordingly
                const company = await api.getCompanyDetails();
                if (cancelled) return;
                const path = location.pathname;
                const isGuarded =
                    /\/drawdown\/(waitlist|product-pending|product-paused)(\/)?$/.test(path);
                if (company?.status === 2) {
                    if (company.drawdown_status === 0) {
                        if (path !== '/drawdown/waitlist') {
                            navigate('/drawdown/waitlist');
                        }
                        setIsLoading(false);
                        setApproval(null);
                        setRates([]);
                        return;
                    } else if (company.drawdown_status === 1) {
                        if (path !== '/drawdown/product-pending') {
                            navigate('/drawdown/product-pending');
                        }
                        setIsLoading(false);
                        setApproval(null);
                        setRates([]);
                        return;
                    } else if (company.drawdown_status === 3) {
                        if (path !== '/drawdown/product-paused') {
                            navigate('/drawdown/product-paused');
                        }
                        setIsLoading(false);
                        setApproval(null);
                        setRates([]);
                        return;
                    } else if (isGuarded) {
                        navigate('/drawdown');
                    }
                } else if (isGuarded) {
                    navigate('/drawdown');
                }

                const [approval, rates] = await api.getDrawDownApproval();

                if (!approval || !rates) {
                    setIsLoading(false);
                    setApproval(null);
                    setRates([]);
                    return;
                }
                setApproval(approval);
                setRates(rates);

                const maxApprovalRate = rates[0];
                if (!maxApprovalRate) {
                    setIsLoading(false);
                    return;
                }

                setRate(maxApprovalRate);

                setWithdrawalData(prev => ({
                    ...prev,
                    factor_rate: maxApprovalRate.daily_rate * maxApprovalRate.day + 1,
                    origination_fee_percentage: approval.origination_fee_percentage,
                    term_length: approval.max_days_outstanding,
                }));

                // Deals are fetched by the paginated table; no need to fetch here

                setTimeout(() => {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }, 100);
            } catch (error) {
                if (!cancelled) {
                    setIsLoading(false);
                    setApproval(null);
                    setRates([]);
                }
            }
        };
        getInfo();
        return () => {
            cancelled = true;
        };
    }, [api]);

    useEffect(() => {
        const updateApproval = async () => {
            setIsLoading(true);
            const [approval] = await api.getDrawDownApproval();

            setApproval(approval);
            setIsLoading(false);
            setDealCreated(false);
        };
        if (dealCreated) {
            updateApproval();
        }
    }, [dealCreated]);

    const setWithdrawal = amount => {
        const netFundedAmount = amount * (1 - withdrawalData.origination_fee_percentage);
        const totalPayment = amount * (rate.daily_rate * withdrawalData.term_length + 1);
        const weeklyPayment = totalPayment / (withdrawalData?.term_length / 7);

        setWithdrawalData({
            ...withdrawalData,
            withdrawal_amount: amount,
            net_funded_amount: netFundedAmount,
            weekly_pmt: weeklyPayment,
            total_pmt: totalPayment,
        });
    };

    const activate = async () => {
        try {
            await api.activateDrawdown();
            navigate('/drawdown');
        } catch (e) {
            console.log(e);
        }
    };

    return (
        <>
            {isLoading && <FeatureLayout loading />}
            {!isLoading && (
                <Routes>
                    <Route
                        path="/*"
                        element={
                            <DrawDownView
                                rates={rates}
                                approval={approval}
                                withdrawalData={withdrawalData}
                                setWithdrawal={setWithdrawal}
                                placeholder={<Box></Box>}
                                setDealCreated={setDealCreated}
                                rate={rate}
                            />
                        }
                    />
                    {/* <Route path="/activities/detail" element={<TransactionDetails />} />
                    <Route path="/activities" element={<Activities />} />
                    <Route path="/payment" element={<Payment />} /> */}
                    <Route
                        path="/withdrawal"
                        element={
                            <Withdrawal
                                setWithdrawal={setWithdrawal}
                                approval={approval}
                                withdrawalData={withdrawalData}
                                setDealCreated={setDealCreated}
                            />
                        }
                    />
                    <Route
                        path="/withdrawal/review"
                        element={
                            <WithdrawalConfirmation
                                approval={approval}
                                withdrawalData={withdrawalData}
                                setDealCreated={setDealCreated}
                            />
                        }
                    />
                    <Route path="/withdrawal/failure" element={<WithdrawalFailure />} />
                    <Route
                        path="/withdrawal/success"
                        element={<WithdrawalSuccess withdrawalData={withdrawalData} />}
                    />
                    <Route
                        path="/withdrawal/success/:dealId"
                        element={<WithdrawalSuccess withdrawalData={withdrawalData} />}
                    />
                    <Route path="/product-pending" element={<ProductPending />} />
                    <Route path="/product-paused" element={<ProductPaused />} />
                    <Route
                        path="/waitlist"
                        element={<Waitlist product="DrawDown" callback={activate} />}
                    />
                </Routes>
            )}
        </>
    );
};
